'use client';

import { createContext, useContext, PropsWithChildren } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { detailPath } from '@/helpers/offering';
import { isEmbedPath } from '@/helpers/embed-navigation';
import type { OfferingType } from '@/services/offering';

// The query every detail link inside this embed carries, or null outside an
// embed. The list's own `hide` travels with the visitor, so an image hidden in
// the list stays hidden on the page it opens, and `back` is the list URL to
// return to.
const EmbedContext = createContext<string | null>(null);

const buildDetailQuery = (
  pathname: string,
  searchParams: URLSearchParams
): string => {
  const params = new URLSearchParams();
  const hide = searchParams.get('hide');
  if (hide) params.set('hide', hide);
  const search = searchParams.toString();
  params.set('back', search ? `${pathname}?${search}` : pathname);
  return `?${params.toString()}`;
};

// Supplied by the embed layout rather than derived from the pathname, so a
// shared component learns it is framed from where it sits in the tree.
export const EmbedProvider = ({ children }: PropsWithChildren) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  return (
    <EmbedContext.Provider value={buildDetailQuery(pathname, searchParams)}>
      {children}
    </EmbedContext.Provider>
  );
};

export const useIsEmbed = () => useContext(EmbedContext) !== null;

// A list embed hands the page it opens the path to return to. Only an embed
// path is honoured: everything else answers with frame-ancestors 'self' and
// would blank the widget, and the value arrives from whoever wrote the host
// page.
export const useEmbedBackPath = () => {
  const isEmbed = useIsEmbed();
  const back = useSearchParams().get('back');
  return isEmbed && back && isEmbedPath(back) ? back : null;
};

export const useDetailHref = (type: OfferingType, id: string, slug: string) => {
  const detailQuery = useContext(EmbedContext);
  return detailQuery === null
    ? detailPath(type, slug)
    : `/embed/${type}/${id}${detailQuery}`;
};
